import React, { useState, useEffect } from 'react';
import api from './api';
import Spinner from './Spinner';
import CustomAlert from './CustomAlert';
import HamburgerMenu from './HamburgerMenu';
import { useAuth } from './useAuth';
import DailyACTimeTable from './DailyACTimeTable';
import ScheduledInspectionTable from './ScheduledInspectionTable';
import QTDACTimeTable from './QTDACTimeTable';
import NMCSPMCSTable from './NMCSPMCSTable';
import ACIReportTable from './ACIReportTable';
import FlightHourMonthlyTable from './FlightHourMonthlyTable';
import AK0PartInstallManHoursTable from './AK0PartInstallManHoursTable';

const getStyles = (theme) => {
    const isDark = theme === 'dark';
    const colors = {
        bg: isDark ? '#121212' : '#f4f7f6',
        cardBg: isDark ? '#1e1e1e' : '#fff',
        text: isDark ? '#e0e0e0' : '#333',
        subtleText: isDark ? '#a0a0a0' : '#666',
        border: isDark ? '#444' : '#ddd',
        shadow: isDark ? 'rgba(255, 255, 255, 0.1)' : 'rgba(0, 0, 0, 0.1)',
        link: isDark ? '#6cb2f7' : '#007bff',
    };

    return {
        page: { backgroundColor: colors.bg, minHeight: '100vh', padding: '20px' },
        container: { maxWidth: '1200px', margin: '0 auto', fontFamily: 'sans-serif' },
        pageHeader: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' },
        pageTitle: { fontSize: '24px', margin: 0 },
        menuItem: {
            padding: '10px 15px',
            cursor: 'pointer',
            whiteSpace: 'nowrap',
            display: 'block',
            textDecoration: 'none',
            color: colors.text,
        },
        menuItemButton: {
            background: 'none',
            border: 'none',
            padding: '10px 15px',
            width: '100%',
            textAlign: 'left',
            cursor: 'pointer',
            fontSize: '16px',
            fontFamily: 'sans-serif',
            color: colors.text,
        },
        card: { backgroundColor: colors.cardBg, border: `1px solid ${colors.border}`, borderRadius: '8px', padding: '20px', boxShadow: `0 2px 5px ${colors.shadow}`, marginBottom: '20px' },
        cardTitle: { fontSize: '18px', fontWeight: 'bold', marginBottom: '15px', color: colors.text },
        uploadGrid: { display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))', gap: '20px', marginBottom: '20px' },
        uploadButton: { 
            padding: '15px 20px', 
            border: 'none', 
            borderRadius: '8px', 
            cursor: 'pointer', 
            fontSize: '14px', 
            fontWeight: 'bold',
            textAlign: 'center',
            transition: 'all 0.3s ease',
            '&:hover': {
                transform: 'translateY(-2px)',
                boxShadow: '0 4px 8px rgba(0,0,0,0.2)'
            }
        },
        buttonDescription: { fontSize: '12px', marginTop: '8px', color: colors.subtleText, fontStyle: 'italic' },
        modalOverlay: { position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, backgroundColor: 'rgba(0,0,0,0.5)', display: 'flex', justifyContent: 'center', alignItems: 'center', zIndex: 1000 },
        modal: { backgroundColor: colors.cardBg, padding: '20px', borderRadius: '8px', width: '500px', maxWidth: '90vw' },
        formGroup: { marginBottom: '15px' },
        label: { display: 'block', marginBottom: '5px', fontWeight: 'bold', color: colors.text },
        input: { width: '100%', padding: '8px', boxSizing: 'border-box', borderRadius: '4px', border: `1px solid ${colors.border}`, backgroundColor: isDark ? '#333' : '#fff', color: colors.text },
        button: { padding: '10px 15px', border: 'none', borderRadius: '4px', cursor: 'pointer', marginRight: '10px' },
        link: colors.link,
    };
};

// Modal component for uploading reports
const UploadModal = ({ onClose, onUpload, sites, tmsOptions, theme, reportType, reportDescription }) => {
    const styles = getStyles(theme);
    const [file, setFile] = useState(null);
    const [siteId, setSiteId] = useState('');
    const [tmsId, setTmsId] = useState('');

    const handleSubmit = (e) => {
        e.preventDefault();
        if (file && siteId && tmsId) {
            // Validate file type
            const allowedTypes = ['.xlsx', '.xls', '.csv'];
            const fileExtension = file.name.toLowerCase().substring(file.name.lastIndexOf('.'));
            
            if (!allowedTypes.includes(fileExtension)) {
                alert('Please select a valid file type (.xlsx, .xls, or .csv).');
                return;
            }
            
            // Validate file size (limit to 10MB)
            if (file.size > 10 * 1024 * 1024) {
                alert('File size must be less than 10MB.');
                return;
            }
            
            onUpload({ file, site_id: siteId, tms_id: tmsId, report_type: reportType });
            onClose();
        } else {
            alert('Please select a file, site, and TMS.');
        }
    };
    
    return (
         <div style={styles.modalOverlay}>
            <div style={styles.modal}>
                <h2>Upload {reportType}</h2>
                <p style={{...styles.label, marginBottom: '15px', fontStyle: 'italic'}}>{reportDescription}</p>
                <form onSubmit={handleSubmit}>
                    <div style={styles.formGroup}>
                        <label style={styles.label}>Site</label>
                        <select style={styles.input} value={siteId} onChange={(e) => setSiteId(e.target.value)} required>
                            <option value="">Select a Site</option>
                            {Array.isArray(sites) && sites.map(s => <option key={s.id} value={s.id}>{s.name}</option>)}
                        </select>
                    </div>
                    <div style={styles.formGroup}>
                        <label style={styles.label}>TMS</label>
                        <select style={styles.input} value={tmsId} onChange={(e) => setTmsId(e.target.value)} required>
                            <option value="">Select a TMS</option>
                            {Array.isArray(tmsOptions) && tmsOptions.map(t => <option key={t.id} value={t.id}>{t.name}</option>)}
                        </select>
                    </div>
                     <div style={styles.formGroup}>
                        <label style={styles.label}>Report File (.xlsx, .xls, .csv)</label>
                        <input style={styles.input} type="file" onChange={(e) => setFile(e.target.files[0])} accept=".xlsx,.xls,.csv" required />
                    </div>
                    <button type="submit" style={{ ...styles.button, backgroundColor: styles.link, color: 'white' }}>Upload</button>
                    <button type="button" onClick={onClose} style={{...styles.button, backgroundColor: '#6c757d', color: 'white'}}>Cancel</button>
                </form>
            </div>
        </div>
    );
};

// Modal for fleet-wide reference uploads that do not require site/TMS.
const FileOnlyUploadModal = ({ onClose, onUpload, theme, reportType, reportDescription }) => {
    const styles = getStyles(theme);
    const [file, setFile] = useState(null);

    const handleSubmit = (e) => {
        e.preventDefault();
        if (!file) {
            alert('Please select a file.');
            return;
        }

        const allowedTypes = ['.xlsx', '.xls', '.csv'];
        const fileExtension = file.name.toLowerCase().substring(file.name.lastIndexOf('.'));

        if (!allowedTypes.includes(fileExtension)) {
            alert('Please select a valid file type (.xlsx, .xls, or .csv).');
            return;
        }

        if (file.size > 10 * 1024 * 1024) {
            alert('File size must be less than 10MB.');
            return;
        }

        onUpload({ file, report_type: reportType });
        onClose();
    };

    return (
        <div style={styles.modalOverlay}>
            <div style={styles.modal}>
                <h2>Upload {reportType}</h2>
                <p style={{ ...styles.label, marginBottom: '15px', fontStyle: 'italic' }}>{reportDescription}</p>
                <form onSubmit={handleSubmit}>
                    <div style={styles.formGroup}>
                        <label style={styles.label}>Report File (.xlsx, .xls, .csv)</label>
                        <input
                            style={styles.input}
                            type="file"
                            onChange={(e) => setFile(e.target.files[0])}
                            accept=".xlsx,.xls,.csv"
                            required
                        />
                    </div>
                    <button type="submit" style={{ ...styles.button, backgroundColor: styles.link, color: 'white' }}>Upload</button>
                    <button type="button" onClick={onClose} style={{ ...styles.button, backgroundColor: '#6c757d', color: 'white' }}>Cancel</button>
                </form>
            </div>
        </div>
    );
};

// Modal requiring ONLY Site and File
const SiteOnlyUploadModal = ({ onClose, onUpload, sites, theme, reportType, reportDescription }) => {
    const styles = getStyles(theme);
    const [file, setFile] = useState(null);
    const [siteId, setSiteId] = useState('');

    const handleSubmit = (e) => {
        e.preventDefault();
        if (file && siteId) {
            onUpload({ file, site_id: siteId, report_type: reportType });
            onClose();
        } else {
            alert('Please select a site and a file.');
        }
    };

    return (
        <div style={styles.modalOverlay}>
            <div style={styles.modal}>
                <h2>Upload {reportType}</h2>
                <p style={{ ...styles.label, marginBottom: '15px', fontStyle: 'italic' }}>{reportDescription}</p>
                <form onSubmit={handleSubmit}>
                    <div style={styles.formGroup}>
                        <label style={styles.label}>Site</label>
                        <select style={styles.input} value={siteId} onChange={(e) => setSiteId(e.target.value)} required>
                            <option value="">Select a Site</option>
                            {Array.isArray(sites) && sites.map(s => <option key={s.id} value={s.id}>{s.name}</option>)}
                        </select>
                    </div>
                    <div style={styles.formGroup}>
                        <label style={styles.label}>Report File (.xlsx, .xls, .csv)</label>
                        <input style={styles.input} type="file" onChange={(e) => setFile(e.target.files[0])} accept=".xlsx,.xls,.csv" required />
                    </div>
                    <button type="submit" style={{ ...styles.button, backgroundColor: styles.link, color: 'white' }}>Upload</button>
                    <button type="button" onClick={onClose} style={{ ...styles.button, backgroundColor: '#6c757d', color: 'white' }}>Cancel</button>
                </form>
            </div>
        </div>
    );
};

// Helper function to get current quarter
const getCurrentQuarter = () => {
    const now = new Date();
    const month = now.getMonth() + 1; // getMonth() returns 0-11
    const year = now.getFullYear();
    
    if (month >= 1 && month <= 3) {
        return `Q1 ${year} (January - March)`;
    } else if (month >= 4 && month <= 6) {
        return `Q2 ${year} (April - June)`;
    } else if (month >= 7 && month <= 9) {
        return `Q3 ${year} (July - September)`;
    } else {
        return `Q4 ${year} (October - December)`;
    }
};

function UploadDataPage({ theme }) {
    const styles = getStyles(theme);
    const [sites, setSites] = useState([]);
    const [tmsOptions, setTmsOptions] = useState([]);
    const [loading, setLoading] = useState(false);
    const [uploadModal, setUploadModal] = useState({ isOpen: false, reportType: '', reportDescription: '' });
    const [alert, setAlert] = useState({ show: false, message: '', type: '' });
    const [showDailyACTimeTable, setShowDailyACTimeTable] = useState(false);
    const [showScheduledInspectionTable, setShowScheduledInspectionTable] = useState(false);
    const [showQTDACTimeTable, setShowQTDACTimeTable] = useState(false);
    const [showNMCSPMCSTable, setShowNMCSPMCSTable] = useState(false);
    const [showACIReportTable, setShowACIReportTable] = useState(false);
    const [showFlightHourMonthlyTable, setShowFlightHourMonthlyTable] = useState(false);
    const [showAK0ManHoursTable, setShowAK0ManHoursTable] = useState(false);
    const [fileOnlyUploadModal, setFileOnlyUploadModal] = useState({ isOpen: false, reportType: '', reportDescription: '' });
    const { isStaff, groups } = useAuth();
    const [siteOnlyModal, setSiteOnlyModal] = useState({ isOpen: false, reportType: '', reportDescription: '' });

    const canUpload = groups.includes('Supervisor') || groups.includes('Upload') || isStaff;

    // Fetch sites and TMS options
    useEffect(() => {
        api.get('/sites/')
            .then(res => {
                const sitesData = Array.isArray(res.data) ? res.data : (res.data.results || []);
                setSites(sitesData);
            })
            .catch(err => {
                console.error("Error fetching sites:", err);
                setSites([]);
            });
        
        api.get('/tms/')
            .then(res => {
                const tmsData = Array.isArray(res.data) ? res.data : (res.data.results || []);
                setTmsOptions(tmsData);
            })
            .catch(err => {
                console.error("Error fetching TMS options:", err);
                setTmsOptions([]);
            });
    }, []);

    const handleUpload = (formData) => {
        const data = new FormData();
        data.append('file', formData.file);
        data.append('site_id', formData.site_id);
        data.append('tms_id', formData.tms_id);
        data.append('report_type', formData.report_type);

        setLoading(true);
        const uploadUrl = formData.report_type === 'Flight Schedule' ? '/upload-flight-schedule/' : '/upload-report/';
        api.post(uploadUrl, data, { headers: { 'Content-Type': 'multipart/form-data' } })
            .then(res => {
                setAlert({ show: true, message: res.data.message, type: 'success' });
                // If it's a Daily AC Time upload, show the table
                if (formData.report_type === 'Raw Data - Daily AC Time') {
                    setShowDailyACTimeTable(true);
                }
                // If it's a Scheduled Inspection upload, show the table
                if (formData.report_type === 'Raw Data - Sched Insp') {
                    setShowScheduledInspectionTable(true);
                }
                // If it's a QTD AC Time upload, show the table
                if (formData.report_type === 'Raw Data - QTD AC Time') {
                    setShowQTDACTimeTable(true);
                }
                // If it's a NMCS PMCS upload, show the table
                if (formData.report_type === 'NMCS PMCS') {
                    setShowNMCSPMCSTable(true);
                }
                // If it's an ACI Report upload, show the table
                if (formData.report_type === 'ACI Report') {
                    setShowACIReportTable(true);
                }
                // If it's a Flight Hour Monthly upload, show the table
                if (formData.report_type === 'Flight Hour Monthly') {
                    setShowFlightHourMonthlyTable(true);
                }
            })
            .catch(err => {
                console.error('Upload error:', err);
                let errorMsg = 'Failed to upload report.';
                
                if (err.response?.data?.error) {
                    errorMsg = err.response.data.error;
                } else if (err.response?.data?.details) {
                    errorMsg = `${err.response.data.error || 'Upload failed'}. ${err.response.data.details}`;
                } else if (err.response?.status === 500) {
                    errorMsg = 'Server error occurred. Please check your file format and try again.';
                } else if (err.response?.status === 400) {
                    errorMsg = 'Invalid request. Please check your file and selections.';
                }
                
                setAlert({ show: true, message: errorMsg, type: 'error' });
            })
            .finally(() => setLoading(false));
    };

    const handleFileOnlyUpload = (formData) => {
        const data = new FormData();
        data.append('file', formData.file);

        setLoading(true);
        const uploadUrl = formData.report_type === 'High Time Part Catalog'
            ? '/upload-high-time-part-catalog/'
            : '/upload-ak0-man-hours/';
        api.post(uploadUrl, data, { headers: { 'Content-Type': 'multipart/form-data' } })
            .then(res => {
                setAlert({ show: true, message: res.data.message, type: 'success' });
                if (formData.report_type === 'AK0 Man Hour Install') {
                    setShowAK0ManHoursTable(true);
                }
            })
            .catch(err => {
                console.error('Upload error:', err);
                let errorMsg = 'Failed to upload report.';
                if (err.response?.data?.error) {
                    errorMsg = err.response.data.error;
                } else if (err.response?.data?.details) {
                    errorMsg = `${err.response.data.error || 'Upload failed'}. ${err.response.data.details}`;
                }
                setAlert({ show: true, message: errorMsg, type: 'error' });
            })
            .finally(() => setLoading(false));
    };

    const handleSiteOnlyUpload = (formData) => {
        const data = new FormData();
        data.append('file', formData.file);
        data.append('site_id', formData.site_id);

        setLoading(true);
        api.post('/upload-parts-on-hand/', data, { headers: { 'Content-Type': 'multipart/form-data' } })
            .then(res => setAlert({ show: true, message: res.data.message, type: 'success' }))
            .catch(err => setAlert({ show: true, message: err.response?.data?.error || 'Failed to upload.', type: 'error' }))
            .finally(() => setLoading(false));
    };

    const handleDailyACTimeUpload = (reportType, reportDescription) => {
        setUploadModal({ 
            isOpen: true, 
            reportType: reportType, 
            reportDescription: reportDescription 
        });
    };


    if (!canUpload) {
        return (
            <div style={styles.page}>
                <div style={styles.container}>
                    <div style={styles.pageHeader}>
                        <HamburgerMenu theme={theme}/>
                        <h1 style={styles.pageTitle}>Upload Data</h1>
                    </div>
                    <div style={styles.card}>
                        <p>You don't have permission to upload data. Contact your administrator.</p>
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div style={styles.page}>
            <div style={styles.container}>
                <CustomAlert {...alert} onClose={() => setAlert({ show: false })} theme={theme} />
                
                {uploadModal.isOpen && <UploadModal 
                    onClose={() => setUploadModal({ isOpen: false, reportType: '', reportDescription: '' })} 
                    onUpload={handleUpload} 
                    sites={sites} 
                    tmsOptions={tmsOptions} 
                    theme={theme}
                    reportType={uploadModal.reportType}
                    reportDescription={uploadModal.reportDescription}
                />}

                {siteOnlyModal.isOpen && (
                        <SiteOnlyUploadModal
                        onClose={() => setSiteOnlyModal({ isOpen: false, reportType: '', reportDescription: '' })}
                        onUpload={handleSiteOnlyUpload}
                        sites={sites}
                        theme={theme}
                        reportType={siteOnlyModal.reportType}
                        reportDescription={siteOnlyModal.reportDescription}
                />
)}

                {fileOnlyUploadModal.isOpen && (
                    <FileOnlyUploadModal
                        onClose={() => setFileOnlyUploadModal({ isOpen: false, reportType: '', reportDescription: '' })}
                        onUpload={handleFileOnlyUpload}
                        theme={theme}
                        reportType={fileOnlyUploadModal.reportType}
                        reportDescription={fileOnlyUploadModal.reportDescription}
                    />
                )}
                
                <div style={styles.pageHeader}>
                    <HamburgerMenu theme={theme}/>
                    <h1 style={styles.pageTitle}>Upload Data</h1>
                </div>

                {loading && <Spinner />}

                {/* Upload Options Card */}
                <div style={styles.card}>
                    <h2 style={styles.cardTitle}>Upload Reports</h2>
                    <p style={{ color: styles.subtleText, marginBottom: '20px' }}>
                        Select the type of report you want to upload. Each report type serves a specific purpose in the aircraft management system.
                    </p>
                    
                    <div style={styles.uploadGrid}>
                        <button 
                            onClick={() => setUploadModal({ 
                                isOpen: true, 
                                reportType: 'Flight Schedule', 
                                reportDescription: 'Sharp7 CTW flight list export (.xlsx). Filename must contain Flight_List_MM-DD-YYYY or Sched Launch dates will be used.' 
                            })} 
                            style={{...styles.uploadButton, backgroundColor: '#6610f2', color: 'white'}}
                        >
                            Upload Flight Schedule
                            <div style={styles.buttonDescription}>
                                Sharp7 flight list — drives Flight Planning tools
                            </div>
                        </button>

                        <button 
                            onClick={() => handleDailyACTimeUpload('Raw Data - Daily AC Time', 'Contains next phase due and hours to phase (hours remain till phase)')} 
                            style={{...styles.uploadButton, backgroundColor: '#17a2b8', color: 'white'}}
                        >
                            Upload Daily AC Time
                            <div style={styles.buttonDescription}>
                                Contains next phase due and hours to phase
                            </div>
                        </button>

                        <button 
                            onClick={() => setUploadModal({ 
                                isOpen: true, 
                                reportType: 'Raw Data - Sched Insp', 
                                reportDescription: 'Contains modex (last 2-3 numbers of buno), inspection name, tsn (hours on aircraft), schd_expndrt (when inspection is due)' 
                            })} 
                            style={{...styles.uploadButton, backgroundColor: '#28a745', color: 'white'}}
                        >
                            Upload Sched Insp
                            <div style={styles.buttonDescription}>
                                Contains inspection schedules and due dates
                            </div>
                        </button>

                        <button 
                            onClick={() => setUploadModal({ 
                                isOpen: true, 
                                reportType: 'Raw Data - QTD AC Time', 
                                reportDescription: 'Quarterly aircraft time data' 
                            })} 
                            style={{...styles.uploadButton, backgroundColor: '#ffc107', color: 'black'}}
                        >
                            Upload QTD AC Time
                            <div style={styles.buttonDescription}>
                                Quarterly aircraft time data
                            </div>
                        </button>

                        <button 
                            onClick={() => setUploadModal({ 
                                isOpen: true, 
                                reportType: 'NMCS PMCS', 
                                reportDescription: 'Provides Estimated Delivery Date (EDDs) for when a part is going to be delivered for an aircraft' 
                            })} 
                            style={{...styles.uploadButton, backgroundColor: '#dc3545', color: 'white'}}
                        >
                            Upload NMCS PMCS
                            <div style={styles.buttonDescription}>
                                Provides estimated delivery dates for parts
                            </div>
                        </button>

                        <button 
                            onClick={() => setUploadModal({ 
                                isOpen: true, 
                                reportType: 'ACI Report', 
                                reportDescription: 'Contains BUNO and ACI due date information' 
                            })} 
                            style={{...styles.uploadButton, backgroundColor: '#6f42c1', color: 'white'}}
                        >
                            Upload ACI Report
                            <div style={styles.buttonDescription}>
                                Contains BUNO and ACI due date information
                            </div>
                        </button>

                        <button 
                            onClick={() => setUploadModal({ 
                                isOpen: true, 
                                reportType: 'Flight Hour Monthly', 
                                reportDescription: 'Contains flight hour data with organization codes, document numbers, technician codes, aircraft serial numbers, timer codes, and flight hours' 
                            })} 
                            style={{...styles.uploadButton, backgroundColor: '#20c997', color: 'white'}}
                        >
                            Upload Flight Hour Monthly
                            <div style={styles.buttonDescription}>
                                Contains flight hour data with organization codes and document numbers
                            </div>
                        </button>

                        <button
                            onClick={() => setUploadModal({
                                isOpen: true,
                                reportType: 'CRD All',
                                reportDescription: 'Full FOUO CRD export (FOUO_CRD_all). Used for High Time Component, Strut Waterfall (PN 133-8*), and cannibalization donor ranking.',
                            })}
                            style={{ ...styles.uploadButton, backgroundColor: '#546e7a', color: 'white' }}
                        >
                            Upload CRD All
                            <div style={styles.buttonDescription}>
                                Full component remaining-life report
                            </div>
                        </button>

                        <button
                            onClick={() => setFileOnlyUploadModal({
                                isOpen: true,
                                reportType: 'High Time Part Catalog',
                                reportDescription: 'MRC part list (pn / dscrptn_sht, optional wuc). Replaces the High Time Component family mapping so old and new part numbers group together.',
                            })}
                            style={{ ...styles.uploadButton, backgroundColor: '#6c757d', color: 'white' }}
                        >
                            Upload High Time Part Catalog
                            <div style={styles.buttonDescription}>
                                Canonical nomenclature and PN families for High Time
                            </div>
                        </button>

                        <button
                            onClick={() => setFileOnlyUploadModal({
                                isOpen: true,
                                reportType: 'AK0 Man Hour Install',
                                reportDescription: 'Historical workbook with Removed Part Numbers and Installed Part Numbers (Average Mean MH). Replaces the reference table. A Part Number and Avg Man Hours file still updates install hours for matching parts.',
                            })}
                            style={{ ...styles.uploadButton, backgroundColor: '#fd7e14', color: 'white' }}
                        >
                            Upload AK0 Man Hour Install
                            <div style={styles.buttonDescription}>
                                Remove and install man-hours for LTD aircraft build-up
                            </div>
                        </button>

                        <button
                            onClick={() => setSiteOnlyModal({
                                isOpen: true,
                                reportType: 'Parts on Hand',
                                reportDescription: 'Upload current inventory levels. This will replace the previous list for the selected site.'
                            })}
                            style={{ ...styles.uploadButton, backgroundColor: '#0d6efd', color: 'white' }}
                        >
                            Upload Parts on Hand
                            <div style={styles.buttonDescription}>
                                Inventory listings for site location
                            </div>
                        </button>
                    </div>
                </div>

                {/* Daily AC Time Table Toggle */}
                <div style={styles.card}>
                    <h2 style={styles.cardTitle}>Daily AC Time Table</h2>
                    <p style={{ color: styles.subtleText, marginBottom: '15px' }}>
                        View and manage the daily aircraft time table data.
                    </p>
                    <button 
                        onClick={() => setShowDailyACTimeTable(!showDailyACTimeTable)} 
                        style={{...styles.button, backgroundColor: showDailyACTimeTable ? '#6c757d' : '#17a2b8', color: 'white'}}
                    >
                        {showDailyACTimeTable ? 'Hide Daily AC Time Table' : 'Show Daily AC Time Table'}
                    </button>
                </div>

                {/* Daily AC Time Table */}
                {showDailyACTimeTable && (
                    <DailyACTimeTable 
                        theme={theme} 
                        sites={sites} 
                        onUpload={handleDailyACTimeUpload}
                    />
                )}

                {/* Scheduled Inspection Table Toggle */}
                <div style={styles.card}>
                    <h2 style={styles.cardTitle}>Scheduled Inspection Table</h2>
                    <p style={{ color: styles.subtleText, marginBottom: '15px' }}>
                        View and manage the scheduled inspection data.
                    </p>
                    <button 
                        onClick={() => setShowScheduledInspectionTable(!showScheduledInspectionTable)} 
                        style={{...styles.button, backgroundColor: showScheduledInspectionTable ? '#6c757d' : '#28a745', color: 'white'}}
                    >
                        {showScheduledInspectionTable ? 'Hide Scheduled Inspection Table' : 'Show Scheduled Inspection Table'}
                    </button>
                </div>

                {/* Scheduled Inspection Table */}
                {showScheduledInspectionTable && (
                    <ScheduledInspectionTable 
                        theme={theme} 
                        sites={sites} 
                        onUpload={handleDailyACTimeUpload}
                    />
                )}

                {/* QTD AC Time Table Toggle */}
                <div style={styles.card}>
                    <h2 style={styles.cardTitle}>QTD AC Time Table</h2>
                    <p style={{ color: styles.subtleText, marginBottom: '10px' }}>
                        View and manage the quarterly aircraft time table data.
                    </p>
                    <p style={{ color: styles.text, marginBottom: '15px', fontWeight: 'bold', fontSize: '14px' }}>
                        Current Quarter: {getCurrentQuarter()}
                    </p>
                    <button 
                        onClick={() => setShowQTDACTimeTable(!showQTDACTimeTable)} 
                        style={{...styles.button, backgroundColor: showQTDACTimeTable ? '#6c757d' : '#ffc107', color: showQTDACTimeTable ? 'white' : 'black'}}
                    >
                        {showQTDACTimeTable ? 'Hide QTD AC Time Table' : 'Show QTD AC Time Table'}
                    </button>
                </div>

                {/* QTD AC Time Table */}
                {showQTDACTimeTable && (
                    <QTDACTimeTable 
                        theme={theme} 
                        sites={sites} 
                        onUpload={handleDailyACTimeUpload}
                    />
                )}

                {/* NMCS PMCS Table Toggle */}
                <div style={styles.card}>
                    <h2 style={styles.cardTitle}>NMCS PMCS Table</h2>
                    <p style={{ color: styles.subtleText, marginBottom: '15px' }}>
                        View and manage the NMCS PMCS data for estimated delivery dates.
                    </p>
                    <button 
                        onClick={() => setShowNMCSPMCSTable(!showNMCSPMCSTable)} 
                        style={{...styles.button, backgroundColor: showNMCSPMCSTable ? '#6c757d' : '#dc3545', color: 'white'}}
                    >
                        {showNMCSPMCSTable ? 'Hide NMCS PMCS Table' : 'Show NMCS PMCS Table'}
                    </button>
                </div>

                {/* NMCS PMCS Table */}
                {showNMCSPMCSTable && (
                    <NMCSPMCSTable 
                        theme={theme} 
                        sites={sites} 
                        onUpload={handleDailyACTimeUpload}
                    />
                )}

                {/* ACI Report Table Toggle */}
                <div style={styles.card}>
                    <h2 style={styles.cardTitle}>ACI Report Table</h2>
                    <p style={{ color: styles.subtleText, marginBottom: '15px' }}>
                        View and manage the ACI report data for aircraft condition inspections.
                    </p>
                    <button 
                        onClick={() => setShowACIReportTable(!showACIReportTable)} 
                        style={{...styles.button, backgroundColor: showACIReportTable ? '#6c757d' : '#6f42c1', color: 'white'}}
                    >
                        {showACIReportTable ? 'Hide ACI Report Table' : 'Show ACI Report Table'}
                    </button>
                </div>

                {/* ACI Report Table */}
                {showACIReportTable && (
                    <ACIReportTable 
                        theme={theme} 
                        sites={sites} 
                        onUpload={handleDailyACTimeUpload}
                    />
                )}

                {/* Flight Hour Monthly Table Toggle */}
                <div style={styles.card}>
                    <h2 style={styles.cardTitle}>Flight Hour Monthly Table</h2>
                    <p style={{ color: styles.subtleText, marginBottom: '15px' }}>
                        View and manage the flight hour monthly data for aircraft operations.
                    </p>
                    <button 
                        onClick={() => setShowFlightHourMonthlyTable(!showFlightHourMonthlyTable)} 
                        style={{...styles.button, backgroundColor: showFlightHourMonthlyTable ? '#6c757d' : '#20c997', color: 'white'}}
                    >
                        {showFlightHourMonthlyTable ? 'Hide Flight Hour Monthly Table' : 'Show Flight Hour Monthly Table'}
                    </button>
                </div>

                {/* Flight Hour Monthly Table */}
                {showFlightHourMonthlyTable && (
                    <FlightHourMonthlyTable 
                        theme={theme} 
                        sites={sites} 
                        onUpload={handleDailyACTimeUpload}
                    />
                )}

                <div style={styles.card}>
                    <h2 style={styles.cardTitle}>AK0 Man Hour Install Table</h2>
                    <p style={{ color: styles.subtleText, marginBottom: '15px' }}>
                        View uploaded remove and install man-hours used for LTD Aircraft build-up estimates.
                    </p>
                    <button
                        onClick={() => setShowAK0ManHoursTable(!showAK0ManHoursTable)}
                        style={{ ...styles.button, backgroundColor: showAK0ManHoursTable ? '#6c757d' : '#fd7e14', color: 'white' }}
                    >
                        {showAK0ManHoursTable ? 'Hide AK0 Man Hour Install Table' : 'Show AK0 Man Hour Install Table'}
                    </button>
                </div>

                {showAK0ManHoursTable && (
                    <AK0PartInstallManHoursTable
                        theme={theme}
                        onUpload={(reportType, reportDescription) => setFileOnlyUploadModal({
                            isOpen: true,
                            reportType,
                            reportDescription,
                        })}
                    />
                )}
            </div>
        </div>
    );
}

export default UploadDataPage;